import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QaProcessesStyle from "./QaProcesses.module.css";
import StyledButton from "../../shared_components/StyledButton/StyledButton";
import ListHeadingWithSearch from "../../shared_components/ListHeadingWithSearch/ListHeadingWithSearch";
import PaginationGroup from "../../shared_components/PaginationGroup/PaginationGroup";
import Spinner from "../../shared_components/Spinner/Spinner";
import InfoPage from "../InfoPage/InfoPage";
import QaProcessService from "../../services/QaProcessService";
import UserData from "../../services/UserData";
import Utils from "../../utils/Utils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faShieldHalved, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import UserClickTracker from "../../services/UserClickTracker";
import Permissions from "../../services/Permissions";
import CustomToast from "../../shared_components/CustomToast/CustomToast";

const ITEMS_PER_PAGE = 10;

const getQaStatus = (asset) => {
  const stage = QaProcessService.getRecordedQaAssetStage(asset.assetKey);
  if (!stage) {
    return { text: "Briefs", status: "pending", stage: "Briefs" };
  }

  if (stage === "Completed") {
    return { text: "Completed", status: "complete", stage: "Completed" };
  }

  return { text: stage, status: "in-progress", stage };
};

const QaProcesses = () => {
  const navigate = useNavigate();
  const { page } = useParams();
  const userData = useContext(UserData);

  const activePage = Utils.isNull(page) ? 1 : parseInt(page, 10);

  const [loading, updateLoading] = useState(true);
  const [plugins, updatePlugins] = useState([]);
  const [maxPages, updateMaxPages] = useState(1);
  const [error, updateError] = useState(false);
  const [errorMessage, updateErrorMessage] = useState({});
  const [searchText, updateSearchText] = useState("");
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [deletingAssetKey, setDeletingAssetKey] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const isAdmin = Permissions.isAdmin(userData);

  const createNewPlugin = () => {
    navigate("/qa-process/new", { state: { referrer: `/qa-process/${activePage}` } });
  };

  const navigateToAdminSetup = () => {
    navigate("/qa-process/admin");
  };

  const updatePageClicked = (page) => {
    navigate(`/qa-process/${page}`);
    updateLoading(true);
  };

  const getAllAssets = useCallback(async () => {
    try {
      // Only load QA assets created through the QA Processes component
      const allAssets = await QaProcessService.getQaAssets(userData);
      const sortedAssets = QaProcessService.sortQaAssetsNewestFirst(allAssets);

      // Calculate pagination
      const totalItems = sortedAssets.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
      if (activePage > totalPages) {
        navigate(`/qa-process/${totalPages}`);
        updateLoading(false);
        return;
      }
      const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedAssets = sortedAssets.slice(startIndex, endIndex);

      updatePlugins(paginatedAssets);
      updateMaxPages(totalPages);
      updateLoading(false);
      updateError(false);
    } catch (error) {
      updateLoading(false);
      updateError(true);
      updateErrorMessage({
        reason: "Unable to fetch QA processes",
        description: error.message,
      });
    }
  }, [activePage, userData, navigate]);

  const getFoundAssetsFromSearch = useCallback(async () => {
    try {
      // Only search QA assets created through the QA Processes component
      const allAssets = await QaProcessService.getQaAssets(userData);
      const searchLower = searchText.toLowerCase();

      // Filter assets by search text (search in name, assignedQaUser, and sourceBriefUrl)
      const filteredAssets = allAssets.filter((asset) => {
        const nameMatch = asset.name?.toLowerCase().includes(searchLower);
        const userMatch = asset.assignedQaUser?.toLowerCase().includes(searchLower);
        const urlMatch = asset.sourceBriefUrl?.toLowerCase().includes(searchLower);
        return nameMatch || userMatch || urlMatch;
      });

      const sortedAssets = QaProcessService.sortQaAssetsNewestFirst(filteredAssets);

      // Calculate pagination
      const totalItems = sortedAssets.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
      if (activePage > totalPages) {
        navigate(`/qa-process/${totalPages}`);
        updateLoading(false);
        return;
      }
      const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedAssets = sortedAssets.slice(startIndex, endIndex);

      updatePlugins(paginatedAssets);
      updateMaxPages(totalPages);
      updateLoading(false);
      updateError(false);
    } catch (error) {
      updateLoading(false);
      updateError(true);
      updateErrorMessage({
        reason: "Unable to search QA processes",
        description: error.message,
      });
    }
  }, [searchText, activePage, userData, navigate]);

  useEffect(() => {
    if (Utils.isEmpty(searchText)) {
      getAllAssets();
    } else {
      getFoundAssetsFromSearch();
    }
  }, [searchText, activePage, getAllAssets, getFoundAssetsFromSearch]);

  const searchAction = (text) => {
    updateSearchText(text);
    updateLoading(true);
    navigate(`/qa-process/1`);
  };

  const showUserToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const promptDeleteAsset = (asset) => {
    if (!isAdmin || !asset?.assetKey) {
      return;
    }
    setAssetToDelete(asset);
  };

  const closeDeleteAssetModal = () => {
    if (deletingAssetKey) {
      return;
    }
    setAssetToDelete(null);
  };

  const confirmDeleteAsset = async () => {
    if (!assetToDelete?.assetKey || !isAdmin) {
      return;
    }
    const asset = assetToDelete;
    setDeletingAssetKey(asset.assetKey);
    try {
      await QaProcessService.deleteQaAsset(userData, asset.assetKey);
      QaProcessService.clearQaAssetLastAccessed(asset.assetKey);
      QaProcessService.clearQaProgressLocal(asset.assetKey);
      QaProcessService.clearQaAssetStage(asset.assetKey);
      setAssetToDelete(null);
      showUserToast(`"${asset.name || "Asset"}" was removed.`);
      updateLoading(true);
      if (Utils.isEmpty(searchText)) {
        await getAllAssets();
      } else {
        await getFoundAssetsFromSearch();
      }
    } catch (err) {
      showUserToast(err?.message || "Unable to delete asset.");
    } finally {
      setDeletingAssetKey(null);
    }
  };

  const getListView = () => {
    if (loading) {
      return <Spinner />;
    }

    if (error) {
      return (
        <InfoPage
          reason={errorMessage.reason}
          description={errorMessage.description}
        />
      );
    }

    if (plugins.length === 0) {
      return (
        <div className={QaProcessesStyle.empty_state}>
          <p>No QA assets found.</p>
          {Utils.isEmpty(searchText) && (
            <p className={QaProcessesStyle.empty_state_hint}>
              Create your first QA asset to get started.
            </p>
          )}
        </div>
      );
    }

    return (
      <div className={QaProcessesStyle.table}>
        <div className={QaProcessesStyle.header_row}>
          <span>Asset Name</span>
          <span>Last updated by</span>
          <span>QA Status</span>
          <span className={QaProcessesStyle.qa_header}>QA</span>
        </div>
        <div className={QaProcessesStyle.body}>
          {plugins.map((plugin) => {
            const handleQaClick = () => {
              UserClickTracker.registerClick(userData);
              navigate(`/qa-process/detail/${plugin.assetKey}`, {
                state: {
                  referrer: `/qa-process/${activePage}`,
                  asset: plugin,
                  resumeStage: qaStatus.stage,
                }
              });
            };

            const qaStatus = getQaStatus(plugin);

            const handleAssetNameClick = (e) => {
              e.stopPropagation();
              if (plugin.sourceBriefUrl) {
                window.open(plugin.sourceBriefUrl, "_blank", "noopener,noreferrer");
              }
            };

            return (
              <div
                key={plugin.assetKey}
                className={QaProcessesStyle.body_row}
              >
                <span
                  className={`${QaProcessesStyle.asset_name} ${plugin.sourceBriefUrl ? QaProcessesStyle.asset_name_clickable : ""}`}
                  onClick={plugin.sourceBriefUrl ? handleAssetNameClick : undefined}
                  title={plugin.sourceBriefUrl ? "Open in Teamwork" : undefined}
                >
                  {plugin.name}
                </span>
                <span className={QaProcessesStyle.last_updated}>{plugin.lastUpdateBy}</span>
                <span className={QaProcessesStyle.status_cell}>
                  <span className={`${QaProcessesStyle.qa_status} ${QaProcessesStyle[`qa_status_${qaStatus.status}`]}`}>
                    {qaStatus.text}
                  </span>
                </span>
                <span className={QaProcessesStyle.qa_cell}>
                  <span className={QaProcessesStyle.qa_cell_actions}>
                    <OverlayTrigger placement="left" overlay={<Tooltip id={`qa-edit-${plugin.assetKey}`}>QA Checklist</Tooltip>}>
                      <FontAwesomeIcon
                        onClick={handleQaClick}
                        icon={faPen}
                        className={QaProcessesStyle.edit_icon}
                      />
                    </OverlayTrigger>
                    {isAdmin ? (
                      <OverlayTrigger placement="left" overlay={<Tooltip id={`qa-del-${plugin.assetKey}`}>Delete asset (admin)</Tooltip>}>
                        <FontAwesomeIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            promptDeleteAsset(plugin);
                          }}
                          icon={faTrashCan}
                          className={
                            deletingAssetKey === plugin.assetKey
                              ? `${QaProcessesStyle.delete_icon} ${QaProcessesStyle.delete_icon_disabled}`
                              : QaProcessesStyle.delete_icon
                          }
                          role="button"
                          aria-label="Delete QA asset"
                        />
                      </OverlayTrigger>
                    ) : null}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getActionButtonsView = () => {
    const canCreateAsset = !Utils.userIsViewOnly(userData);

    if (!canCreateAsset && !isAdmin) {
      return null;
    }

    return (
      <div className={QaProcessesStyle.actions}>
        {canCreateAsset ? (
          <StyledButton clickEvent={createNewPlugin}>NEW ASSET</StyledButton>
        ) : null}
        {isAdmin ? (
          <StyledButton
            clickEvent={navigateToAdminSetup}
            className={QaProcessesStyle.admin_button}
          >
            <FontAwesomeIcon icon={faShieldHalved} className={QaProcessesStyle.admin_button_icon} />
            ADMIN SETUP
          </StyledButton>
        ) : null}
      </div>
    );
  };

  const getUserViewClass = () => {
    if (Utils.userIsViewOnly(userData)) {
      return QaProcessesStyle.viewer_view;
    }
    return QaProcessesStyle.creator_view;
  };

  const listView = getListView();

  return (
    <div className={`${QaProcessesStyle.container} ${getUserViewClass()}`}>
      {getActionButtonsView()}
      <div className={QaProcessesStyle.list}>
        <ListHeadingWithSearch searchAction={searchAction}>
          QA PROCESSES
        </ListHeadingWithSearch>
        {listView}
        <PaginationGroup
          activePage={activePage}
          numberOfPages={maxPages}
          updatePageClicked={updatePageClicked}
        />
      </div>
      {assetToDelete ? (
        <div
          className={QaProcessesStyle.confirm_overlay}
          role="presentation"
          onClick={closeDeleteAssetModal}
        >
          <div
            className={QaProcessesStyle.confirm_dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qa-delete-asset-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="qa-delete-asset-title" className={QaProcessesStyle.confirm_title}>
              Delete QA asset?
            </h3>
            <p className={QaProcessesStyle.confirm_body}>
              This will permanently remove{" "}
              <strong>{assetToDelete.name || assetToDelete.assetKey}</strong> from QA Processes.
            </p>
            <div className={QaProcessesStyle.confirm_actions}>
              <StyledButton
                clickEvent={closeDeleteAssetModal}
                className={QaProcessesStyle.confirm_cancel_button}
                disabled={Boolean(deletingAssetKey)}
              >
                CANCEL
              </StyledButton>
              <StyledButton
                clickEvent={confirmDeleteAsset}
                className={QaProcessesStyle.confirm_danger_button}
                disabled={Boolean(deletingAssetKey)}
              >
                {deletingAssetKey ? "DELETING…" : "DELETE"}
              </StyledButton>
            </div>
          </div>
        </div>
      ) : null}
      <CustomToast message={toastMessage} showToast={showToast} setShow={setShowToast} />
    </div>
  );
};

export default QaProcesses;

